/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Ticket } from '@touchplan/common';
import { Rectangle } from 'pixi.js-legacy';

export class PullRegion {
	public startDate: string;
	public finishDate: string;
	public startX: number;
	public finishX: number;
	public activeStartX?: number;
	public activeFinishX?: number;
	public calculatedCalendarDays?; //added later
	public tickets: Ticket[] = []; //maybe create a "ColumnTicket" so it can be decorated with additional data

	public in(left: number, right: number) {
		return left < this.finishX && right > this.startX;
	}
	public push(ticket: Ticket) {
		let grew = false;
		let right = ticket.view.dragStartData.x + ticket.view.dragStartData.width;

		if (this.tickets.length === 0) {
			this.startX = ticket.view.dragStartData.x;
			this.finishX = right;
		} else {
			if (ticket.view.dragStartData.x < this.startX) {
				this.startX = ticket.view.dragStartData.x;
				grew = true;
			}
			if (right > this.finishX) {
				this.finishX = right;
				grew = true;
			}
		}

		this.tickets.push(ticket);

		return grew && this.tickets.length > 1; //growing from empty regions is pointless
	}
}

//TODO - there's currently a pretty hard dependency on the ticket drag start data, see about removing that
export class PullCalculation {
	public regions: PullRegion[];
	public activeRect?: Rectangle;
	public pullStartRect?: Rectangle;
	public get startDate() {
		if (this.regions.length) {
			return this.regions[this.regions.length - 1].startDate || null;
		}
		return null;
	}

	constructor(tickets: Map<string, Ticket>) {
		this.recalc(tickets);
	}

	public recalc(tickets: Map<string, Ticket>) {
		this.regions = []; //maybe don't clear it?

		let top = Infinity;
		tickets.forEach((t) => {
			let tLeft = t.view.dragStartData.x;
			let tRight = t.view.dragStartData.x + t.view.dragStartData.width;
			let handled = false;
			//special handling for the first one
			if (!this.regions.length) {
				let region = new PullRegion();
				region.push(t);
				this.regions.push(region);
				handled = true;
			} else {
				let existingRegion;
				for (let idx = 0; idx < this.regions.length; idx++) {
					let region = this.regions[idx];
					//insert at the beginning
					if (tLeft >= region.finishX) {
						existingRegion = new PullRegion();
						this.regions.splice(idx, 0, existingRegion);
						break;
					}
					//or in a region
					if (region.in(tLeft, tRight)) {
						existingRegion = region;
						break;
					}
				}
				//or at the end
				if (!existingRegion) {
					existingRegion = new PullRegion();
					this.regions.push(existingRegion);
				}
				let grew = existingRegion.push(t);
				if (grew) {
					this.smooshRegions();
				}
			}

			if (t.view.dragStartData.y < top) {
				top = t.view.dragStartData.y;
			}
		});
		this.pullStartRect = new Rectangle(
			this.regions[0].startX,
			top,
			this.regions[this.regions.length - 1].finishX - this.regions[0].startX,
			0
		);
		//(this.startRect.y + pullSnappedY) + (pullTicketHeight + snappingDistance) * ticket.view.dragStartData.yOrder
	}

	public iterate(
		cb: (ticket: Ticket, region: PullRegion, ticketIndex?: number, regionIndex?: number) => void
	) {
		for (let i = this.regions.length - 1; i >= 0; i--) {
			let r = this.regions[i];
			r.tickets.forEach((ticket, idx) => {
				cb(ticket, r, idx, i);
			});
		}
	}

	private smooshRegions() {
		let prev;
		for (let idx = 0; idx < this.regions.length; idx++) {
			let region = this.regions[idx];
			if (!prev) {
				prev = region;
				continue;
			} //first
			if (region.finishX > prev.startX) {
				prev.startX = region.startX;
				prev.tickets = [...prev.tickets, ...region.tickets];
				this.regions.splice(idx, 1);
				this.smooshRegions();
				break;
			}
		}
	}
}
